import React, { useEffect, useState } from "react";
import Mousetrap from "mousetrap";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { DndProvider } from "react-dnd";

import When from "./utils/When";
import Toast from "./base/Toast";
import DevTools from "./DevTools";
import config from "../config/app";
import JSONEditor from "./JSONEditor";
import DesignView from "./DesignView";
import ErrorSaving from "./ErrorSaving";
import OfflineView from "./OfflineView";
import LoadingView from "./LoadingView";
import AppStore from "../stores/AppStore";
import AppErrorView from "./AppErrorView";
import SubscribeView from "./SubscribeView";
import BlurredOverlay from "./BlurredOverlay";
import AppActions from "../actions/AppActions";
import Login from "../app2components/login/Login";
import { queryClient } from "../utils/QueryUtils";
import DesignImportModal from "./DesignImportModal";
import UserSiteChooser from "./UserSiteChooser/UserSiteChooserView";
import AppConstants from "../constants/AppConstants";
import ActionConfirmModal from "./ActionConfirmModal";
import DesignManager from "../managers/DesignManager";
import ModalController from "./utils/ModalController";
import NotSupportedView from "./UnsupportedBrowserView";
import LibrarySiteChooser from "./LibrarySiteChooserView";
import { useProcessingState } from "../utils/ProcessingStateUtils";
import styles from "./LoadingView.module.css";
import LocalStorageService from "../service/LocalStorageService";
import { isIOS } from "../constants/BrowserConstants";
import TouchDragPreview from "./base/TouchDragPreview";

const {
  MAIN_VIEW_ERROR,
  MAIN_VIEW_DESIGN,
  MAIN_VIEW_LOGIN,
  MODAL_DESIGN_IMPORT,
  MODAL_ACTION_CONFIRM,
  MAIN_VIEW_LOADING,
  MAIN_VIEW_BROWSER,
  MAIN_VIEW_LIBRARY_SITE_CHOOSER,
  MAIN_VIEW_USER_SITE_CHOOSER,
  MAIN_VIEW_OFFLINE,
  MAIN_VIEW_SUBSCRIBE,
} = AppConstants;

const mousetrap = new Mousetrap();

// iPad has no HTML5 drag-and-drop for touch. A finger held 200ms starts a
// drag, so a quicker swipe still scrolls the panels; mouse events keep a
// trackpad or mouse dragging too.
const dndBackend = isIOS ? TouchBackend : HTML5Backend;
const dndOptions = isIOS ? { enableMouseEvents: true, delayTouchStart: 200 } : undefined;

const preventBrowser = (e) => {
  e.preventDefault();
  e.stopPropagation();
  return false;
};

const addKeyListeners = () => {
  mousetrap.bind(
    [
      "del",
      "backspace",
      "mod+z",
      "mod+v",
      "shift+mod+z",
      "mod+y",
      "mod+-",
      "mod+=",
      "mod+p",
      "mod+l",
    ],
    preventBrowser,
  );
};

const removeKeyListeners = () => {
  mousetrap.reset();
};

const App = () => {
  const [app, setApp] = useState(AppStore.getState());
  const processingState = useProcessingState("app");

  const _onAppChange = (passedApp) => {
    if (app !== passedApp) {
      setApp(passedApp);
    }
  };

  const addEventListeners = () => {
    window.onbeforeunload = (e) => {
      if (DesignManager.needsToSave() && !DesignManager.inError) {
        return "You have unsaved work, Are you sure you want to leave?";
      }
    };
    document.addEventListener("processingStateChange", () => console.log(processingState));
  };

  const _loadModal = (modalName, modalComponentName) => {
    const modal = app.getIn(["modals", modalName], false);

    return ModalController.getModal(this, modalName, modal, modalComponentName);
  };

  useEffect(() => {
    (async () => {
      const response = await fetch("/config");
      const appConfig = await response.json();
      const appInfo = document.getElementById("app-info");
      const { appVersion, dataModelVersion } = appInfo?.dataset;
      AppActions.initialize({ ...appConfig, ver: appVersion, dataModelVersion: +dataModelVersion });
    })();

    const storeUnsubscribe = AppStore.listen(_onAppChange);

    addEventListeners();
    addKeyListeners();

    return () => {
      storeUnsubscribe && storeUnsubscribe();
      removeKeyListeners();
    };
  }, []);

  const style = {
    height: "100%",
  };

  if (config.debugJSON) {
    return <JSONEditor show container={this} debugMode />;
  }

  let mainView = app.get("mainView");
  const viewProps = app.get("mainViewProps");
  const savingError = app.get("savingError");
  const appError = app.get("appError");
  const hasPreviousView = AppStore.hasPreviousView();
  const showitDevTools = LocalStorageService.get(AppConstants.LOCAL_STORE_DEV_TOOLS) === "true";

  const importModal = _loadModal(MODAL_DESIGN_IMPORT, DesignImportModal);
  const actionConfirmModal = _loadModal(MODAL_ACTION_CONFIRM, ActionConfirmModal);

  let view = null;

  // override whatever the main view is to show the error
  if (appError) {
    mainView = MAIN_VIEW_ERROR;
  }

  switch (mainView) {
    case MAIN_VIEW_DESIGN:
      view = <DesignView />;
      break;
    case MAIN_VIEW_LOGIN:
      view = <Login />;
      break;
    case MAIN_VIEW_LOADING:
      view = <LoadingView className={styles.loadingViewMain} />;
      break;
    case MAIN_VIEW_BROWSER:
      view = <NotSupportedView reasonNotSupported={config.reasonNotSupported} />;
      break;
    case MAIN_VIEW_LIBRARY_SITE_CHOOSER:
      view = <LibrarySiteChooser closeEnabled={hasPreviousView} {...viewProps} />;
      break;
    case MAIN_VIEW_USER_SITE_CHOOSER:
      view = <UserSiteChooser closeEnabled={hasPreviousView} {...viewProps} />;
      break;
    case MAIN_VIEW_OFFLINE:
      view = <OfflineView />;
      break;
    case MAIN_VIEW_ERROR:
      view = <AppErrorView error={appError} />;
      break;
    case MAIN_VIEW_SUBSCRIBE:
      view = <SubscribeView />;
      break;
    default:
  }

  let errorView = null;
  if (savingError) {
    errorView = <ErrorSaving error={savingError} />;
  }

  const { overlay, message, error } = processingState;

  return (
    <>
      {errorView}
      <div id="showitapp" style={style} key="showitapp">
        <QueryClientProvider client={queryClient}>
          <DndProvider backend={dndBackend} options={dndOptions}>
            <When it={isIOS}>
              <TouchDragPreview />
            </When>
            <Toast />
            {view}
            {importModal}
            {actionConfirmModal}

            <When it={showitDevTools && mainView === MAIN_VIEW_DESIGN}>
              <DevTools />
            </When>
          </DndProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
        <BlurredOverlay
          show={overlay}
          styleOverride={{ height: "100vh", position: "relative", zIndex: 99 }}
        >
          {(onClose) => (
            <LoadingView
              message={<p style={{ marginTop: "3em" }}>{message}</p>}
              error={typeof error === "function" ? error(onClose) : error}
            />
          )}
        </BlurredOverlay>
      </div>
    </>
  );
};

export default App;
